import { useTranslation } from 'react-i18next';
import { driftMinutes, isBackdated, isNoticeableDrift } from '@construction/shared';
import { Tag } from '@/components/ui';

/**
 * 「这条记录是什么时候真的进系统的」（CR-033）。
 *
 * 客户原话：「现在我意识到我这个系统所有记录，除了打卡。其他都是基于自选时间对吧？
 * 这类软件不是应该制作成自动记录时间嘛？」
 *
 * 系统一直在记（`createdAt` / `serverAt` / `uploadedAt` / `receivedAt`），
 * 只是四个页面都没显示，所以看起来像全靠自选。这个组件是那句话的答案：
 * **人填的日期旁边永远摆着系统记的时刻**，对不上就标出来。
 *
 * 两种用法：
 * - 提交类（日报、采购）：`recordedAt` + `businessDay` → 「提交于 …」，
 *   人填的日期和提交那天不是同一天时标「补录」；
 * - 拍摄类（隐蔽工程、照片）：`recordedAt` + `capturedAt` → 「拍摄于 … · 服务器 …」，
 *   两者差得多时标「补传」。
 *
 * **时刻一律按工地时区渲染**（`createdAt` 这些是真时刻）。只有 `workDate` 那种
 * 「只表示哪一天」的值才按 UTC 渲染 —— 两种值混着渲染会整列日期退一天（CR-031 踩过）。
 */
export function RecordTime({
  recordedAt,
  timeZone,
  businessDay,
  capturedAt,
  bare = false,
  className,
}: {
  /** 服务端记下的时刻。ISO 字符串 */
  recordedAt: string;
  /** 工地时区 */
  timeZone: string;
  /** 人填的业务日期（YYYY-MM-DD 或带时刻的 ISO）。给了就判断「补录」 */
  businessDay?: string;
  /** 设备给的拍摄时刻。给了就显示两个时间并判断「补传」 */
  capturedAt?: string | null;
  /** 表格里用：表头已经写着「提交时间」，格子里只要时刻本身 */
  bare?: boolean;
  className?: string;
}) {
  const { t, i18n } = useTranslation();

  const recorded = new Date(recordedAt);
  const fmt = (at: Date) =>
    at.toLocaleString(i18n.language, {
      timeZone,
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  if (capturedAt) {
    const captured = new Date(capturedAt);
    const late = isNoticeableDrift(captured, recorded);
    return (
      <span className={className}>
        <span className="text-caption tabular-nums text-ink-3">
          {bare ? fmt(captured) : t('records.capturedAt', { time: fmt(captured) })}
          {' · '}
          {bare ? fmt(recorded) : t('records.receivedAt', { time: fmt(recorded) })}
        </span>
        {late && (
          <span className="ml-2 align-middle">
            {/*
              差几分钟一起写出来：只说「补传」而不说差多少，
              看的人没法判断这是弱网晚了半小时，还是设备时钟被调过。
            */}
            <Tag>{t('records.lateUpload', { minutes: driftMinutes(captured, recorded) })}</Tag>
          </span>
        )}
      </span>
    );
  }

  const backdated = businessDay ? isBackdated(businessDay, recorded, timeZone) : false;

  return (
    <span className={className}>
      <span className="text-caption tabular-nums text-ink-3">
        {bare ? fmt(recorded) : t('records.submittedAt', { time: fmt(recorded) })}
      </span>
      {backdated && (
        <span className="ml-2 align-middle">
          <Tag>{t('records.backdated')}</Tag>
        </span>
      )}
    </span>
  );
}

/**
 * 只要那个「补录 / 补传」徽章，不要时间那一行 —— 给折叠行用。
 *
 * 折叠行的宽度是按列定死的（CR-032），塞不下完整时刻；
 * 但「这条是事后填的」必须在折叠状态下就看得见，否则要一条条点开才发现。
 */
export function RecordFlag({
  recordedAt,
  timeZone,
  businessDay,
  capturedAt,
}: {
  recordedAt: string;
  timeZone: string;
  businessDay?: string;
  capturedAt?: string | null;
}) {
  const { t } = useTranslation();
  const recorded = new Date(recordedAt);

  if (capturedAt) {
    const captured = new Date(capturedAt);
    if (!isNoticeableDrift(captured, recorded)) return null;
    return <Tag>{t('records.lateUploadShort')}</Tag>;
  }

  if (!businessDay || !isBackdated(businessDay, recorded, timeZone)) return null;
  return <Tag>{t('records.backdated')}</Tag>;
}
